import { useSelector } from "react-redux";
import { Fragment, useEffect, useState } from "react";
import { Dialog } from "@mui/material";
import { useDialog } from "app/hook";
import { RootState } from "app/store";
import { DialogType, RestrictedFeatureSource } from "app/types";
import I18n from "common/i18n";
import { useScale, btnDelay, usePlaySound } from "common/utils";
import { CloseBtn, FadeTransition, getPlanFullI18nKey } from "common/elements";
import { PlanType } from "./types";
import { usePlan, usePlanLimits, useAccountLimits } from "./hooks";

// COURSE は GUEST(=TRIAL) 専用の制限で ParentRegistrationPromptDialog 側に出るため、
// RestrictedFeatureDialog では扱わない
const getMessageKeys = (
  source: RestrictedFeatureSource,
): { titleKey: string; messageKey: string } | null => {
  switch (source) {
    case RestrictedFeatureSource.PROJECT_LIMIT:
      return {
        titleKey: "MSG_RESTRICTED_FEATURE_TITLE_PROJECT_LIMIT",
        messageKey: "MSG_RESTRICTED_FEATURE_MESSAGE_PROJECT_LIMIT",
      };
    case RestrictedFeatureSource.CHILD_USER_LIMIT:
      return {
        titleKey: "MSG_RESTRICTED_FEATURE_TITLE_CHILD_USER_LIMIT",
        messageKey: "MSG_RESTRICTED_FEATURE_MESSAGE_CHILD_USER_LIMIT",
      };
    case RestrictedFeatureSource.AI_CHAT_LIMIT:
      return {
        titleKey: "MSG_RESTRICTED_FEATURE_TITLE_AI_CHAT_LIMIT",
        messageKey: "MSG_RESTRICTED_FEATURE_MESSAGE_AI_CHAT_LIMIT",
      };
    case RestrictedFeatureSource.AI_IMAGE_LIMIT:
      return {
        titleKey: "MSG_RESTRICTED_FEATURE_TITLE_AI_IMAGE_LIMIT",
        messageKey: "MSG_RESTRICTED_FEATURE_MESSAGE_AI_IMAGE_LIMIT",
      };
    case RestrictedFeatureSource.ASSET_LIMIT:
      return {
        titleKey: "MSG_RESTRICTED_FEATURE_TITLE_ASSET_LIMIT",
        messageKey: "MSG_RESTRICTED_FEATURE_MESSAGE_ASSET_LIMIT",
      };
    case RestrictedFeatureSource.COURSE:
      return null;
  }
};

export const RestrictedFeatureDialog = () => {
  const play = usePlaySound();
  const plan = usePlan();
  const limits = usePlanLimits();
  const accountLimits = useAccountLimits();
  const { scale } = useScale();
  const handleDialog = useDialog();
  const [titleKey, setTitleKey] = useState("");
  const [messageKey, setMessageKey] = useState("");
  const [source, setSource] = useState<RestrictedFeatureSource | undefined>(undefined);
  const redirect = useSelector((state: RootState) => state.config.dialog.restricted_feature);
  const visible = redirect.value && !!titleKey;

  // PREMIUM/BUILDER/EDUCATION はアップグレード促進しない
  const noPromotion =
    plan === PlanType.PREMIUM || plan === PlanType.BUILDER || plan === PlanType.EDUCATION;

  useEffect(() => {
    if (redirect.value) {
      const src = redirect.args?.source as RestrictedFeatureSource | undefined;
      if (!src) return;
      const keys = getMessageKeys(src);
      if (!keys) return;
      setSource(src);
      setTitleKey(keys.titleKey);
      setMessageKey(keys.messageKey);
    }
  }, [redirect]);

  // バイト数を MB / GB の表示文字列に変換
  const formatBytes = (bytes: number): string => {
    const mb = bytes / (1024 * 1024);
    if (mb >= 1024) {
      const gb = mb / 1024;
      return Number.isInteger(gb) ? `${gb}GB` : `${gb.toFixed(1)}GB`;
    }
    return `${Math.round(mb)}MB`;
  };

  // source に応じて limit プレースホルダの表示値を取得
  const getLimit = (): number | string => {
    switch (source) {
      case RestrictedFeatureSource.PROJECT_LIMIT:
        return limits.projects.limit;
      case RestrictedFeatureSource.CHILD_USER_LIMIT:
        return accountLimits.players.limit;
      case RestrictedFeatureSource.AI_CHAT_LIMIT:
        return limits.aiChatDaily.limit;
      case RestrictedFeatureSource.AI_IMAGE_LIMIT:
        return limits.aiImageDaily.limit;
      case RestrictedFeatureSource.ASSET_LIMIT:
        return formatBytes(limits.assets.limit);
      default:
        return 0;
    }
  };

  const handleClose = () => {
    play();
    btnDelay(() => {
      handleDialog({ type: DialogType.RESTRICTED_FEATURE, value: false });
    });
  };

  const handleAction = () => {
    play();
    btnDelay(() => {
      handleDialog({ type: DialogType.RESTRICTED_FEATURE, value: false });
      if (!noPromotion) {
        handleDialog({ type: DialogType.UPGRADE_PROMOTION, value: true });
      }
    });
  };

  // アクションボタンのラベルとアイコンを決定
  const getActionButton = () => {
    if (noPromotion) {
      return null; // PREMIUM/BUILDER/EDUCATION は閉じるボタンのみ
    }
    return {
      label: I18n.t("MSG_RESTRICTED_FEATURE_BTN_VIEW_PLANS"),
      icon: "workspace_premium",
    };
  };

  const actionButton = getActionButton();

  return (
    <Dialog
      open={visible}
      maxWidth={false}
      sx={{ zIndex: 9999999999 }}
      TransitionComponent={FadeTransition}
      componentsProps={{
        backdrop: { style: { backgroundColor: "transparent" } },
      }}
      PaperProps={{
        style: {
          margin: 0,
          overflow: "visible",
          backgroundColor: "transparent",
        },
        elevation: 0,
      }}
    >
      <div className="flex-col-el flex-center relative" style={{ transform: `scale(${scale})` }}>
        <div
          className="flex-col-el flex-center relative"
          style={{
            filter:
              "drop-shadow(0px 6px 6px rgb(0 0 0 / 20%)) drop-shadow(0px 10px 14px rgb(0 0 0 / 14%)) drop-shadow(0px 4px 18px rgb(0 0 0 / 12%))",
          }}
        >
          <div className="flex-col-el justify-start w-[650px] relative border-solid border-white border-[8px] rounded-lg bg-white">
            <CloseBtn close={handleClose} />

            <div className="flex-col-view justify-start w-full h-full relative p-6 rounded-[4px] bg-beige-60">
              <div className="flex-row-view items-center justify-start pb-4">
                <div className="flex-col-center mr-2">
                  <span className="material-symbols-outlined type-title text-[#4C9DF1]">info</span>
                </div>
                <p className="text-body text-textcolor-black type-title">{I18n.t(titleKey)}</p>
              </div>

              <div className="flex-col-view items-start py-4">
                {I18n.t(messageKey, {
                  limit: getLimit(),
                  plan: I18n.t(getPlanFullI18nKey(plan)),
                })
                  .split("\n")
                  .map((line, index) => {
                    return (
                      <div key={index} className="flex-row-view flex-wrap mb-3">
                        <p className="text-body text-textcolor-black type-body">・</p>
                        <p className="text-body text-textcolor-black type-body flex-1">
                          {line.split("<br>").map((part, i, arr) => (
                            <Fragment key={i}>
                              {part}
                              {i < arr.length - 1 && <br />}
                            </Fragment>
                          ))}
                        </p>
                      </div>
                    );
                  })}
              </div>

              <div className="flex-row-el flex-center gap-6 mt-4 px-0 alpha">
                {noPromotion ? (
                  // PREMIUM/BUILDER/EDUCATION: 閉じるボタンのみ
                  <button className="btn btn-primary" onClick={handleClose}>
                    <div className="flex-row-center gap-1 mb-[3px]">
                      <span className="material-symbols-outlined !text-4xl text-white">close</span>
                      <p className="text-body text-white text-font-size-body1">
                        {I18n.t("MSG_CLOSE_BTN")}
                      </p>
                    </div>
                  </button>
                ) : (
                  // TRIAL/FREE/STANDARD: あとで + アクションボタン
                  <>
                    <button className="btn btn-blue" onClick={handleClose}>
                      <div className="flex-row-center gap-1 mb-[3px]">
                        <span className="material-symbols-outlined !text-4xl text-white">
                          close
                        </span>
                        <p className="text-body text-white text-font-size-body1">
                          {I18n.t("MSG_BTN_LATER")}
                        </p>
                      </div>
                    </button>

                    {actionButton && (
                      <button className="btn btn-primary" onClick={handleAction}>
                        <div className="flex-row-center gap-1 mb-[3px]">
                          <span className="material-symbols-outlined !text-4xl text-white">
                            {actionButton.icon}
                          </span>
                          <p className="text-body text-white text-font-size-body1">
                            {actionButton.label}
                          </p>
                        </div>
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
